/**
 * Split plain chat text into segments for safe rendering with clickable http(s) URLs.
 * Used only when the message is treated as plain text (no markdown).
 */

export function trimTrailingPunctuation(url) {
    return String(url || '').replace(/[),.;:!?'")\]}>]+$/g, '');
}

function isSafeHttpUrl(href) {
    try {
        const u = new URL(href);
        return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * Turn a bare `www.example.com/path` or `https://...` fragment into a safe href, or "".
 */
export function normalizePlainUrlCandidateToHref(raw) {
    const t = trimTrailingPunctuation(String(raw || '').trim());
    if (!t) return '';
    if (/^https?:\/\//i.test(t)) return isSafeHttpUrl(t) ? t : '';
    if (/^www\./i.test(t)) {
        const withProto = `https://${t}`;
        if (!isSafeHttpUrl(withProto)) return '';
        try {
            const { hostname } = new URL(withProto);
            if (!hostname.includes('.')) return '';
            return withProto;
        } catch {
            return '';
        }
    }
    return '';
}

/**
 * @param {string} text
 * @returns {Array<{ type: 'text' | 'link'; value: string; label?: string }>}
 * For links, `value` is the normalized href; `label` is what the user typed (e.g. `www...` without scheme).
 */
export function splitPlainTextWithUrls(text) {
    const s = String(text ?? '');
    if (!s) return [];
    const re = /\b(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    const out = [];
    let last = 0;
    let m;
    while ((m = re.exec(s))) {
        const start = m.index;
        if (start > last) out.push({ type: 'text', value: s.slice(last, start) });
        const raw = m[0];
        const display = trimTrailingPunctuation(raw);
        const href = normalizePlainUrlCandidateToHref(raw);
        if (href) out.push({ type: 'link', value: href, label: display });
        else out.push({ type: 'text', value: raw });
        last = start + raw.length;
    }
    if (last < s.length) out.push({ type: 'text', value: s.slice(last) });
    return out.length ? out : [{ type: 'text', value: s }];
}
